import React, { Fragment, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import DropdownMenu from '#@/components/DropdownMenu';
import HoverableElement from '#@/components/HoverableElement';
import { useTranslations } from '#@/modules/translations';
import { DynamicOverflow } from '#@/plumbing/DynamicOverflow';
import type { GetVisibleElements } from '#@/plumbing/DynamicOverflow/types';
import { sizes } from '#@/shared/utils/styles';

import Breadcrumb from './Breadcrumb';
import { CHEVRON_ICON_SIZE, ChevronIcon, GAP, MoreIcon, Wrapper } from './Styles';
import type { BreadcrumbData } from './types';

export interface BreadcrumbsProps {
  container: HTMLElement;
  items: { path?: string; label: string }[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ container, items }) => {
  const history = useHistory();
  const overflowApiRef = useRef<{ refresh: () => void }>(null);

  const { overflowButtonLabel } = useTranslations('breadcrumbs');

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => overflowApiRef.current?.refresh());

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [container, overflowApiRef]);

  const getVisibleElements: GetVisibleElements<BreadcrumbData> = (elements) => {
    const containerBoundaries = container.getBoundingClientRect();

    const allElementsWidth = elements.reduce((sum, element) => sum + element.boundaries.width, 0);
    const allChevronIconsWidth = CHEVRON_ICON_SIZE * (elements.length - 1);
    const allGaps = 2 * GAP * (elements.length - 1);

    if (allElementsWidth + allChevronIconsWidth + allGaps <= containerBoundaries.width) {
      return elements.map(({ elementKey }) => elementKey);
    }

    const firstElementWidth = elements[0].boundaries.width + GAP + CHEVRON_ICON_SIZE;
    const moreButtonSize = sizes.iconPaddedSize + CHEVRON_ICON_SIZE + GAP;
    const lastElementWidth = elements[elements.length - 1].boundaries.width;

    const availableSpace =
      containerBoundaries.width - firstElementWidth - moreButtonSize - lastElementWidth;
    let sum = 0;
    const result: string[] = [];

    // We have to iterate in reverse because breadcrumbs should be hidden
    // from the left, not the right.
    [...elements].reverse().forEach(({ elementKey, boundaries }, index) => {
      // The first and last breadcrumb should always be shown.
      if (index === 0 || index === elements.length - 1) {
        result.push(elementKey);
        return;
      }

      sum += boundaries.width + GAP + CHEVRON_ICON_SIZE + GAP;
      if (sum < availableSpace) {
        result.push(elementKey);
      }
    });

    return result;
  };

  return (
    <Wrapper>
      <DynamicOverflow<BreadcrumbData>
        forwardRef={overflowApiRef}
        getVisibleElements={getVisibleElements}
      >
        {({ overflowElements }) =>
          items.map(({ path, label }, index) => (
            <Fragment key={path || index}>
              {index === 1 && overflowElements.length > 0 && (
                <>
                  <DropdownMenu.Container
                    placement="bottom-start"
                    renderTrigger={({ toggleTrigger }) => (
                      <HoverableElement isTriggerElementClickable tooltipText={overflowButtonLabel}>
                        <MoreIcon onClick={toggleTrigger} />
                      </HoverableElement>
                    )}
                  >
                    {overflowElements.map(({ elementKey, data: { to, label: elementLabel } }) => (
                      <DropdownMenu.Action
                        key={elementKey}
                        onClick={to ? () => history.push(to) : undefined}
                      >
                        {elementLabel}
                      </DropdownMenu.Action>
                    ))}
                  </DropdownMenu.Container>
                  <ChevronIcon />
                </>
              )}
              {(index !== 1 || overflowElements.length === 0) && (
                <Breadcrumb index={index} to={path} label={label} totalItems={items.length} />
              )}
            </Fragment>
          ))
        }
      </DynamicOverflow>
    </Wrapper>
  );
};

export default Breadcrumbs;
